import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  skipUntil,
  switchMap,
  Observable,
  tap,
  Subject,
  take,
  takeUntil,
  concatMap,
  of,
  shareReplay,
  catchError,
  defer,
  map,
  scan,
  repeat,
  NEVER,
} from 'rxjs';
import type {ObservableInput} from 'rxjs';
import {stringify} from 'query-string';
import {getLocationSeachParam} from '@/helper/url';
import {mc} from '@/core';
import {browser$} from './browser';

// #region createSyncLocationObservable: 创建一个始终和链接地址保持同步的observable
interface SyncLocationParams {
  defaultValue: string;
  locationId: string;
  getHistoryAction?: (params: {currentValue: string; nextValue: string}) => 'pushState' | 'replaceState';
}
export function createSyncLocationObservable(params: SyncLocationParams) {
  const {defaultValue, locationId, getHistoryAction} = params;

  const syncLocation$ = new BehaviorSubject(defaultValue);
  const stop$ = new Subject();

  let isFirst = true;
  syncLocation$
    .pipe(
      skipUntil(browser$),
      filter((value) => !(isFirst && !value)),
      distinctUntilChanged(),
      takeUntil(stop$)
    )
    .subscribe((value) => {
      const currentValue = getLocationSeachParam(locationId)?.trim();
      isFirst = false;
      if (currentValue !== value) {
        const historyAction = (getHistoryAction && getHistoryAction({currentValue: currentValue, nextValue: value})) || 'replaceState';
        const searchParams = value !== null && value !== '' ? `?${stringify({[locationId]: value})}` : '';
        history[historyAction]({}, '', `${location.pathname}${searchParams}`);
      }
    });

  browser$.pipe(take(1), takeUntil(stop$)).subscribe(() => {
    const currentValue = getLocationSeachParam(locationId)?.trim();
    if (currentValue !== defaultValue) {
      syncLocation$.next(currentValue);
    }
  });

  return {
    syncLocation$,
    stop$,
  };
}
// #endregion

// #region createPollObservable: 创建轮询的Observable
type PollAction<T> = (params: T extends undefined ? any : T) => ObservableInput<any>;
interface PollConfigs<A> {
  /**
   * 轮询间隔
   * @default 5000
   */
  pollInterval?: number;
  /**
   * 轮询次数
   * @default Infinity
   */
  pollTimes?: number;
  stopPolling$?: Observable<any>;
  /** 重新激活原本的订阅 */
  reactive$?: Observable<any>;
  /** 根据返回值来判断是否停止轮询 */
  shouldStopPollingAction?: (params: InferObservableInput<A>) => boolean;
}
type InferObservableInput<T> = T extends ObservableInput<infer I> ? I : never;
/**
 * 创建轮询的Observable
 * @param pollAction 轮询执行的动作
 * @param pollConfigs 轮询配置
 */
export function createPollObservable<T, A extends PollAction<T>>(pollAction: A, pollConfigs: PollConfigs<A>) {
  type NotifierParams = Parameters<A>['0'] extends undefined ? void : Parameters<A>['0'];
  const notifier$ = new Subject<NotifierParams>();
  const {pollTimes, pollInterval, stopPolling$, reactive$, shouldStopPollingAction} = {
    ...getDefaultPollParams(),
    ...pollConfigs,
  };

  const stopObservable = stopPolling$ || new Subject();
  let lastNotificationData;
  const context = {
    timer: null,
  };

  const poll$ = notifier$.pipe(
    tap((data) => {
      lastNotificationData = data;
      if (context.timer) {
        clearTimeout(context.timer);
      }
    }),
    takeUntil(stopObservable),
    repeat({
      delay: () => reactive$,
    }),
    switchMap(pollAction),
    tap((response) => {
      const shouldStop = shouldStopPollingAction && shouldStopPollingAction(response);
      if (context.timer) {
        clearTimeout(context.timer);
      }
      if (!shouldStop) {
        context.timer = setTimeout(() => {
          notifier$.next(lastNotificationData);
          clearTimeout(context.timer);
        }, pollInterval);
      }
    }),
    take(pollTimes),
    repeat({
      delay: () => reactive$,
    }),
    shareReplay(1)
  );

  return {
    notifier$,
    poll$,
    context,
  };
}

function getDefaultPollParams() {
  return {
    pollInterval: 5000,
    pollTimes: Infinity,
    reactive$: NEVER,
  };
}
// #endregion

// #region execObservableSteps: 按照顺序执行用数组维护的流

interface ObservableStep<T extends unknown> {
  name: string;
  generateObservable: (params: T, context?: any) => Observable<unknown | never>;
}
/**
 * 按照顺序执行用数组维护的流
 * @param steps 顺序执行的步骤
 * @param params 步骤接收的参数
 */
export function execObservableSteps<T>(steps: Array<ObservableStep<T>>, params: T) {
  return steps
    .reduce<Observable<unknown>>((observable$, step) => {
      return observable$.pipe(concatMap((context) => step.generateObservable(params, context)));
    }, of(params))
    .pipe(
      catchError((error: CustomObservableError) => {
        if (error.type !== 'SKIP_STEP' && error?.message) {
          mc.send('toast', 'error', error);
        }
        throw error;
      })
    );
}
export function defineObservableStep<T extends unknown>(observableSteps: ObservableStep<T>) {
  return observableSteps;
}
export function defineObservableSteps<T extends unknown>(observableSteps: Array<ObservableStep<T>>) {
  return observableSteps;
}
/** 提取 */
export type ExtractObservableStepParams<T extends ObservableStep<any>> = Parameters<T['generateObservable']>['0'];
export class CustomObservableError extends Error {
  public type: CustomObservableErrorOptions['type'] = 'SKIP_STEP';

  constructor(message: string, options?: CustomObservableErrorOptions) {
    super(message);
    const type = options?.type;
    if (type) {
      this.type = type;
    }
  }
}
interface CustomObservableErrorOptions {
  /**
   * SKIP_STEP: 仅仅用来跳过后续step
   */
  type?: 'SKIP_STEP';
}
// #endregion

// #region tapOnce：仅执行一次副作用的operator
export function tapOnce<T>(callback: (data: T) => void) {
  return function factory(source$: Observable<T>) {
    return defer(() => {
      let first = true;
      return source$.pipe(
        tap((data: T) => {
          if (first) {
            callback(data);
          }
          first = false;
        })
      );
    });
  };
}
// #endregion

// #region onlyOnce：全局：仅执行一次副作用的operator
export function onlyOnce<T>(callback: (data: T) => void) {
  return function factory(source$: Observable<T>) {
    let first = true;
    return source$.pipe(
      tap((data: T) => {
        if (first) {
          callback(data);
        }
        first = false;
      })
    );
  };
}
// #endregion

// #region waitEffect: 相当于tap，但是可以返回一个ObservableInput，会等待这个ObservableInput输出
export function waitEffect<T>(callback: (data: T) => ObservableInput<any>) {
  return function factory(source$: Observable<T>) {
    return source$.pipe(
      switchMap((data: T) =>
        defer(() => callback(data)).pipe(
          take(1),
          map(() => data)
        )
      )
    );
  };
}
// #endregion

// include bot type 'SPOT' | 'CONTRACT'
export function isStrategySubUser(subUser) {
  return subUser.userBizType === 'BOT_TRADE_ACCOUNT';
}

export function isCtSubUser(subUser) {
  return subUser.userBizType === 'CT_FOLLOWER';
}

export function showRequestionErrorMessage(response) {
  const error = response.error;
  if (error) {
    mc.send('toast', 'error', error);
  }
}

/**
 * 创建一个清空值的shareReplayObservable
 */
export function createSharedObservable<D, T extends ShareReplayObservableParams<D>>(initData: D, params: T): T['data$'] {
  const {reactive$, clear$, data$} = params;
  const newData$ = new BehaviorSubject(initData);

  let execedEffect = false;
  clear$.subscribe(() => {
    newData$.next(initData);
  });

  const sourceIsRxSubject = isRxSubject(data$);

  /** 如果源是一个subject类型，需要提前订阅，避免有数据流发送出去之后，才开始订阅 */
  if (sourceIsRxSubject) {
    createSubscription();
  }

  return newData$.pipe(
    tap(() => {
      /**
       * 保证只有在observable被订阅的时候，才会触发源observable的订阅
       * clear触发之后，清除掉源observable的订阅
       * active触发之后，重新激活原始订阅
       */
      if (!execedEffect) {
        createSubscription();
      }
    })
  ) as T['data$'];

  function createSubscription() {
    execedEffect = true;
    data$
      .pipe(
        takeUntil(clear$),
        repeat({
          delay: () => reactive$,
        })
      )
      .subscribe((data) => {
        newData$.next(data);
      });
  }
}

function isRxSubject(obj) {
  return typeof obj.next === 'function' && typeof obj.error === 'function' && typeof obj.complete === 'function';
}

interface ShareReplayObservableParams<D> {
  data$: Observable<D>;
  reactive$: Observable<any>;
  clear$: Observable<any>;
}
